import { axios } from '@lib/axios';
import { Locale } from 'chaika-enums';

import type {
    EntryPrintHtmlResponse,
    EntryPrintHtmlData,
    EntryPrintFormDataParams,
    EntryPrintHtmlCreateParams,
    AllEntryPrintFormParams,
} from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';

const BASE_URL = '/api/entry';

interface GetPdfAsBufferArrayProps {
    entryId: string;
    entryData: EntryPrintFormDataParams;
}

interface GetPdfByHTMLAsBufferArrayProps {
    html: string;
}

export class EntryPrintFormApi {
    public static async getPdfAsBufferArrayFromHtml(props: GetPdfByHTMLAsBufferArrayProps): Promise<ArrayBuffer> {
        const blob = await this.getEntryPDFByHtmlStringAsBlob(props.html);
        return blob.arrayBuffer();
    }

    public static async generatePdfAsBufferArray({
        entryId,
        entryData,
    }: GetPdfAsBufferArrayProps): Promise<ArrayBuffer> {
        const blob = await this.getPdfAsBlob(entryId, entryData);
        return blob.arrayBuffer();
    }

    public static async getPdfAsBlob(entryId: string, entryData: EntryPrintFormDataParams): Promise<Blob> {
        const response = await axios.put<Blob>(
            `${BASE_URL}/${entryId}/pdf`,
            {
                entryData,
            },
            { responseType: 'blob', timeout: 0 },
        );
        return response.data;
    }

    public static async getEntryPDFByHtmlStringAsBlob(html: string): Promise<Blob> {
        const response = await axios.post<Blob>(
            `${BASE_URL}/generate/pdf`,
            {
                html,
            },
            { responseType: 'blob', timeout: 0 },
        );
        return response.data;
    }

    public static async createHtmlString(entryId: string, entryData: EntryPrintFormDataParams): Promise<string> {
        const response = await axios.put<{ entryHtml: string }>(`${BASE_URL}/${entryId}/html`, { entryData });

        return response.data.entryHtml;
    }

    public static async makeEntryHtmlParams(
        entryId: string,
        params: AllEntryPrintFormParams,
    ): Promise<Omit<EntryPrintHtmlCreateParams, 'locale'>> {
        const response = await axios.put<Omit<EntryPrintHtmlCreateParams, 'locale'>>(
            `${BASE_URL}/${entryId}/html/params`,
            params,
        );

        return response.data;
    }

    public static async getEntryHtmlString(entryId: string, locale: Locale): Promise<Optional<EntryPrintHtmlData>> {
        const response = await axios.get<EntryPrintHtmlResponse>(`${BASE_URL}/${entryId}/print/html`, {
            params: { locale },
            skipForbiddenPopup: true,
        });

        return response.data.entryPrintHtml;
    }

    public static async deleteEntryHtml(entryId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${entryId}/print/html`);
    }

    public static async updateEntryHtmlString(entryId: string, params: EntryPrintHtmlCreateParams): Promise<void> {
        await axios.put(`${BASE_URL}/${entryId}/print/html`, params);
    }
}
